import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLine } from "./Primitives";
import { EASE, scrollToId } from "../../lib/site";

const Tilt = ({ children }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>{children}</motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bigX = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] overflow-hidden pt-24 md:pt-28" data-testid="hero-section">
      <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <motion.div style={{ x: bigX }} aria-hidden
        className="pointer-events-none absolute -bottom-6 left-0 whitespace-nowrap font-display text-[34vw] font-900 uppercase leading-none text-outline opacity-60 md:text-[22vw]">
        Motta Motta
      </motion.div>

      <div className="relative mx-auto grid max-w-[1400px] gap-10 px-5 md:px-10 lg:grid-cols-12 lg:gap-6">
        <div className="relative z-10 lg:col-span-7 lg:pt-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-300">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--red)] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--red)]" /></span>
            Agenda aberta · Aula experimental
          </motion.div>

          <h1 className="font-display text-[18vw] font-900 uppercase leading-[0.86] tracking-tight sm:text-[14vw] lg:text-[6.8rem] xl:text-[7.6rem]" data-testid="hero-title">
            <MaskLine delay={0.3}>Treino</MaskLine>
            <MaskLine delay={0.42}><span className="text-[var(--red)]">com</span> método.</MaskLine>
            <MaskLine delay={0.54} className="text-outline !text-transparent">Resultado</MaskLine>
            <MaskLine delay={0.66}>de verdade.</MaskLine>
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end">
            <p className="max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">
              Personal trainer presencial, consultoria online e mista — com foco também no treinamento feminino. Um plano desenhado para o seu corpo, sua rotina e o seu objetivo.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("agendar")} data-testid="hero-cta-agendar"
              className="group relative flex items-center gap-3 overflow-hidden bg-[var(--red)] px-7 py-5 font-display text-xl font-800 uppercase tracking-wider text-[#0a0a0c]">
              <span className="absolute inset-0 -translate-x-full bg-[var(--bone)] transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative">Agendar aula experimental</span>
              <ArrowUpRight className="relative h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button onClick={() => scrollToId("servicos")} data-testid="hero-cta-servicos"
              className="flex items-center gap-2 border-b border-white/30 py-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-200 transition-colors hover:border-[var(--red)] hover:text-white">
              Ver serviços <ArrowDownRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}>
            <Tilt>
              <div className="clip-notch relative aspect-[4/5] overflow-hidden bg-[var(--ink-3)] lg:mt-6">
                <motion.img style={{ y: imgY, scale: 1.2 }} src="/img/hero.jpg" alt="Mulher treinando com barra em academia"
                  className="h-full w-full object-cover object-[40%_50%]" data-testid="hero-image" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_55%_35%,transparent_20%,rgba(10,10,12,0.85)_75%)]" />
                <div className="absolute inset-0 mix-blend-color bg-[var(--red)] opacity-15" />
                <div className="absolute left-5 top-5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-200">CM / 01 — Força</div>
                <div className="absolute bottom-6 left-5 right-12 flex items-end justify-between" style={{ transform: "translateZ(60px)" }}>
                  <div className="font-display text-5xl font-900 uppercase leading-none">1:1</div>
                  <div className="text-right font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">Atendimento<br />individual</div>
                </div>
              </div>
            </Tilt>
          </motion.div>
          <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 1.4, duration: 1, ease: EASE }}
            className="absolute -left-10 top-[42%] hidden h-28 w-28 md:block">
            <svg viewBox="0 0 100 100" className="h-full w-full animate-[spin_18s_linear_infinite]">
              <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text className="font-mono" fontSize="9.5" letterSpacing="3" fill="#F4F1EA"><textPath href="#c">AULA EXPERIMENTAL • AULA EXPERIMENTAL • </textPath></text>
            </svg>
            <div className="absolute inset-0 m-auto h-3 w-3 rounded-full bg-[var(--red)]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
